import { useEffect, useState } from 'react';
import { parseAssignedGradeSplitIds, parseAssignedGrades } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';
import { SUPER_ADMIN_PERMISSION, isSchoolAdminUser, isSuperAdminUser } from '../lib/adminAccess';
import { buildSchoolGradeOptions } from '../lib/schoolGradeOptions';
import PhotoUploadField from '../components/PhotoUploadField';

const PERMISSIONS = ['TEACHER', 'ADMIN', 'SUPER_ADMIN/SUPPORT'];

function buildStaffForm(staff) {
  if (!staff) {
    return {
      f_name: '', l_name: '', school_email: '', mobile_phone: '',
      position_title: '', permission_level: 'TEACHER', is_active: '1',
      password: '', photo_relative_path: null,
    };
  }

  return {
    f_name: staff.f_name || '',
    l_name: staff.l_name || '',
    school_email: staff.school_email || '',
    mobile_phone: staff.mobile_phone || '',
    position_title: staff.position_title || '',
    permission_level: staff.permission_level || 'TEACHER',
    is_active: staff.is_active ? '1' : '0',
    password: '',
    photo_relative_path: staff.photo_relative_path || null,
  };
}

function buildSelectedGrades(staff) {
  return new Set(parseAssignedGrades(staff?.assigned_grades));
}

function buildSelectedGradeSplitIds(staff) {
  return new Set(parseAssignedGradeSplitIds(staff?.assigned_grade_split_ids));
}

function isActive(value) {
  return value === true || value === 1 || String(value) === '1';
}

export default function StaffForm({ token, navigate, schoolId, staff, staffId, user }) {
  const editStaffId = staff?.id || staffId || null;
  const isEdit = !!editStaffId;
  const [staffRecord, setStaffRecord] = useState(staff || null);
  const effectiveSchoolId = schoolId || staff?.school_id || staffRecord?.school_id;
  const schoolScopedUser = isSchoolAdminUser(user);
  const canManageSuperAdminStaff = isSuperAdminUser(user);
  const availablePermissions = schoolScopedUser
    ? PERMISSIONS.filter((permission) => permission !== SUPER_ADMIN_PERMISSION)
    : PERMISSIONS;
  const recordSchoolMismatch = Boolean(
    staffRecord?.school_id
    && effectiveSchoolId
    && Number(staffRecord.school_id) !== Number(effectiveSchoolId)
  );

  const [form, setForm] = useState(() => buildStaffForm(staff));

  const [grades, setGrades] = useState([]);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [studentGroupingMode, setStudentGroupingMode] = useState('grades');
  const [selectedGrades, setSelectedGrades] = useState(() => buildSelectedGrades(staff));
  const [selectedGradeSplitIds, setSelectedGradeSplitIds] = useState(() => buildSelectedGradeSplitIds(staff));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(isEdit && !staff);

  useEffect(() => {
    if (!staff) return;
    setStaffRecord(staff);
    setForm(buildStaffForm(staff));
    setSelectedGrades(buildSelectedGrades(staff));
    setSelectedGradeSplitIds(buildSelectedGradeSplitIds(staff));
    setLoading(false);
  }, [staff]);

  useEffect(() => {
    if (!isEdit || staff || !editStaffId) return undefined;

    let cancelled = false;
    setLoading(true);

    apiFetch(`/staff/${editStaffId}`, {}, token)
      .then((res) => {
        if (cancelled) return;
        setStaffRecord(res.data);
        setForm(buildStaffForm(res.data));
        setSelectedGrades(buildSelectedGrades(res.data));
        setSelectedGradeSplitIds(buildSelectedGradeSplitIds(res.data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editStaffId, isEdit, staff, token]);

  useEffect(() => {
    if (!effectiveSchoolId) return;
    Promise.all([
      apiFetch(`/schools/${effectiveSchoolId}`, {}, token).catch(() => ({ data: null })),
      apiFetch(`/school-grade-names?school_id=${effectiveSchoolId}`, {}, token).catch(() => ({ data: [] })),
      apiFetch(`/school-grade-splits?school_id=${effectiveSchoolId}&is_active=true`, {}, token).catch(() => ({ data: [] })),
      apiFetch(`/students?school_id=${effectiveSchoolId}`, {}, token).catch(() => ({ data: [] })),
    ]).then(([schoolRes, gradeNamesRes, gradeSplitsRes, studentsRes]) => {
      const activeSplits = (gradeSplitsRes.data || [])
        .filter((split) => isActive(split.is_active))
        .sort((left, right) => (
          Number(left.grade) - Number(right.grade)
          || String(left.split_code || '').localeCompare(String(right.split_code || ''))
        ));
      setStudentGroupingMode(schoolRes.data?.student_grouping_mode || 'grades');
      setGradeSplits(activeSplits);
      setGrades(buildSchoolGradeOptions(
        gradeNamesRes.data || [],
        [...(studentsRes.data || []), ...activeSplits]
      ));
    });
  }, [effectiveSchoolId, token]);

  const set = (field) => (e) => setForm((currentForm) => ({ ...currentForm, [field]: e.target.value }));

  const toggleGrade = (gradeCode) => {
    setSelectedGrades((previousGrades) => {
      const nextGrades = new Set(previousGrades);
      nextGrades.has(gradeCode) ? nextGrades.delete(gradeCode) : nextGrades.add(gradeCode);
      return nextGrades;
    });
  };

  const toggleGradeSplit = (splitId) => {
    setSelectedGradeSplitIds((previousIds) => {
      const nextIds = new Set(previousIds);
      nextIds.has(splitId) ? nextIds.delete(splitId) : nextIds.add(splitId);
      return nextIds;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (recordSchoolMismatch) {
      setError('This staff member does not belong to the selected school.');
      return;
    }

    if (schoolScopedUser && form.permission_level === SUPER_ADMIN_PERMISSION) {
      setError('Only super admins can create or edit support administrator accounts.');
      return;
    }

    setSaving(true);
    try {
      const body = {
        school_id: effectiveSchoolId,
        f_name: form.f_name,
        l_name: form.l_name,
        school_email: form.school_email || null,
        mobile_phone: form.mobile_phone,
        position_title: form.position_title || null,
        permission_level: form.permission_level,
        is_active: parseInt(form.is_active),
        assigned_grades: form.permission_level === 'TEACHER' && selectedGrades.size > 0
          ? [...selectedGrades]
          : null,
        assigned_grade_split_ids: form.permission_level === 'TEACHER' && selectedGradeSplitIds.size > 0
          ? [...selectedGradeSplitIds]
          : null,
        photo_relative_path: form.photo_relative_path || null,
      };

      if (form.password) {
        body.password = form.password;
      }

      if (isEdit) {
        await apiFetch(`/staff/${staffRecord?.id || editStaffId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }, token);
      } else {
        if (!form.password) {
          setError('Password is required for new staff members');
          setSaving(false);
          return;
        }
        await apiFetch('/staff', {
          method: 'POST',
          body: JSON.stringify(body),
        }, token);
      }
      navigate('staff', effectiveSchoolId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const accountId = staffRecord?.id || editStaffId;
    if (!isEdit || !accountId) return;

    setError('');

    if (recordSchoolMismatch) {
      setError('This staff member does not belong to the selected school.');
      return;
    }

    if (staffRecord?.permission_level === SUPER_ADMIN_PERMISSION && !canManageSuperAdminStaff) {
      setError('Only super admins can delete support administrator accounts.');
      return;
    }

    const fullName = `${staffRecord?.f_name || form.f_name || ''} ${staffRecord?.l_name || form.l_name || ''}`.trim() || 'this staff member';
    if (!window.confirm(`Delete staff account "${fullName}"? This will deactivate the account and prevent login.`)) return;

    setDeleting(true);
    try {
      await apiFetch(`/staff/${accountId}`, { method: 'DELETE' }, token);
      navigate('staff', effectiveSchoolId);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const isTeacher = form.permission_level === 'TEACHER';
  const showGradeSplits = studentGroupingMode === 'splits_only' || studentGroupingMode === 'grades_and_splits';
  const gradeLabelByCode = Object.fromEntries(grades.map((grade) => [Number(grade.grade_code), grade.label]));

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (isEdit && !staffRecord) return <div className="sa-error">Staff member not found</div>;
  if (recordSchoolMismatch) return <div className="sa-error">Staff member not found for this school</div>;
  if (staffRecord?.permission_level === SUPER_ADMIN_PERMISSION && !canManageSuperAdminStaff) {
    return <div className="sa-error">Only super admins can edit support administrator accounts.</div>;
  }

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        <button onClick={() => navigate('staff', effectiveSchoolId)}>Staff</button>
        {' / '}
        {isEdit ? `Edit: ${staffRecord?.f_name || ''} ${staffRecord?.l_name || ''}`.trim() : 'Add Staff'}
      </div>

      <div className="sa-card">
        <h2>{isEdit ? 'Edit Staff Member' : 'Add Staff Member'}</h2>

        {error && <div className="sa-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <PhotoUploadField
            value={form.photo_relative_path}
            onChange={(path) => setForm((prev) => ({ ...prev, photo_relative_path: path }))}
            token={token}
          />

          <div className="sa-form-grid">
            <div className="sa-field">
              <label>First Name *</label>
              <input value={form.f_name} onChange={set('f_name')} required />
            </div>
            <div className="sa-field">
              <label>Last Name *</label>
              <input value={form.l_name} onChange={set('l_name')} required />
            </div>
            <div className="sa-field">
              <label>School Email</label>
              <input type="email" value={form.school_email} onChange={set('school_email')} />
            </div>
            <div className="sa-field">
              <label>Mobile Phone *</label>
              <input value={form.mobile_phone} onChange={set('mobile_phone')} required />
            </div>
            <div className="sa-field">
              <label>Position Title</label>
              <input value={form.position_title} onChange={set('position_title')} />
            </div>
            <div className="sa-field">
              <label>Permission Level *</label>
              <select value={form.permission_level} onChange={set('permission_level')}>
                {availablePermissions.map((permission) => (
                  <option key={permission} value={permission}>{permission.replace('/', ' / ')}</option>
                ))}
              </select>
            </div>
            <div className="sa-field">
              <label>Active</label>
              <select value={form.is_active} onChange={set('is_active')}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div className="sa-field">
              <label>{isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
              <input
                type="password"
                value={form.password}
                onChange={set('password')}
                placeholder={isEdit ? 'Leave blank to keep current' : 'Enter password'}
                required={!isEdit}
              />
            </div>
          </div>

          {isTeacher && (grades.length > 0 || (showGradeSplits && gradeSplits.length > 0)) && (
            <div style={{ marginTop: 20 }}>
              <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                Assigned Classes / Grades
              </label>
              <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px' }}>
                {showGradeSplits
                  ? 'Select a grade for full-grade coverage. You can also assign individual class splits below.'
                  : 'Select the grades this teacher is responsible for. These will be pre-selected on the dismissal board.'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {grades.map((grade) => (
                  <button
                    key={grade.grade_code}
                    type="button"
                    onClick={() => toggleGrade(grade.grade_code)}
                    aria-pressed={selectedGrades.has(grade.grade_code)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: selectedGrades.has(grade.grade_code) ? '2px solid #4a90d9' : '2px solid #ddd',
                      background: selectedGrades.has(grade.grade_code) ? '#4a90d9' : '#fff',
                      color: selectedGrades.has(grade.grade_code) ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: selectedGrades.has(grade.grade_code) ? 600 : 400,
                    }}
                  >
                    {grade.label}
                  </button>
                ))}
              </div>

              {showGradeSplits && gradeSplits.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                    Class Splits
                  </label>
                  <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px' }}>
                    Select specific splits when this teacher should not cover the entire grade.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {gradeSplits.map((split) => {
                      const splitId = Number(split.id);
                      const selected = selectedGradeSplitIds.has(splitId);
                      const gradeLabel = gradeLabelByCode[Number(split.grade)];
                      const label = studentGroupingMode === 'splits_only'
                        ? split.split_code
                        : `${gradeLabel || `Grade ${split.grade}`} - ${split.split_code}`;

                      return (
                        <button
                          key={split.id}
                          type="button"
                          onClick={() => toggleGradeSplit(splitId)}
                          aria-pressed={selected}
                          style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: selected ? '2px solid #4a90d9' : '2px solid #ddd',
                            background: selected ? '#4a90d9' : '#fff',
                            color: selected ? '#fff' : '#333',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: selected ? 600 : 400,
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <div className="sa-btn-row" style={{ marginTop: 24 }}>
            <button type="submit" className="sa-btn sa-btn-primary" disabled={saving || deleting}>
              {saving ? 'Saving...' : isEdit ? 'Update Staff' : 'Create Staff'}
            </button>
            <button
              type="button"
              className="sa-btn sa-btn-secondary"
              onClick={() => navigate('staff', effectiveSchoolId)}
              disabled={saving || deleting}
            >
              Cancel
            </button>
            {isEdit ? (
              <button
                type="button"
                className="sa-btn sa-btn-danger"
                onClick={handleDeleteAccount}
                disabled={saving || deleting}
                style={{ marginLeft: 'auto' }}
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
